import { SuperInput } from "@components:SuperInput/SuperInput";
import { MenuItem, SxProps } from "@mui/material";

import { PrimitiveNode } from "../types";
import { SecretInput } from "./SecretInput";

/*
 * The input for one primitive value, chosen from the node's input kind.
 * Used by PrimitiveField and by the item rows of arrays and maps.
 */
export const PrimitiveInput = ({
  node,
  value,
  onChange,
  label,
  errorText,
  disabled,
  placeholder,
  sx,
}: {
  node: PrimitiveNode;
  value: unknown;
  onChange: (value: unknown) => void;
  label: string;
  errorText?: string;
  disabled?: boolean;
  placeholder?: string;
  sx?: SxProps;
}) => {
  const a11y = { slotProps: { htmlInput: { "aria-label": label } } };
  const extraSx = Array.isArray(sx) ? sx : [sx];
  const selectSx: SxProps = [
    { width: "100%", "& .SuperInput__Select": { width: "100%" } },
    ...extraSx.filter(Boolean),
  ];

  switch (node.input) {
    case "switch":
      return (
        <SuperInput.Switch
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
          inputProps={{ "aria-label": label }}
          errorText={errorText}
          sx={sx}
        />
      );

    case "select":
      return (
        <SuperInput.Select
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          inputProps={{ "aria-label": label }}
          errorText={errorText}
          sx={selectSx}
        >
          {node.options.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </SuperInput.Select>
      );

    case "number":
      return (
        <SuperInput.NumberField
          value={value ?? ""}
          min={node.minimum}
          max={node.maximum}
          step={node.integer ? 1 : 0.01}
          onChange={(e) => {
            const raw = e.target.value;
            onChange(raw === "" ? null : Number(raw));
          }}
          disabled={disabled}
          placeholder={placeholder}
          errorText={errorText}
          sx={sx}
          {...a11y}
        />
      );

    case "password":
      return (
        <SecretInput
          path={node.path}
          value={value}
          onChange={onChange}
          label={label}
          errorText={errorText}
          disabled={disabled}
          sx={sx}
        />
      );

    default:
      return (
        <SuperInput.TextField
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          placeholder={placeholder}
          errorText={errorText}
          sx={sx}
          {...a11y}
        />
      );
  }
};
